// Minimal typed fetch wrapper. Mirrors the existing api() function used by the
// legacy HTML pages: throws on non-2xx with the FastAPI `detail` field as the
// error message, returns null on 204 / empty bodies, otherwise parses JSON.

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export interface AuthHeaderProvider {
  (): Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function createApiClient(baseUrl: string, authHeaders: AuthHeaderProvider) {
  return async function api<T = unknown>(
    path: string,
    { method = "GET", body, signal, query }: ApiOptions = {},
  ): Promise<T> {
    let url = baseUrl + path;
    if (query) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null) continue;
        params.append(k, String(v));
      }
      const qs = params.toString();
      if (qs) url += (url.includes("?") ? "&" : "?") + qs;
    }

    const headers: Record<string, string> = { ...authHeaders() };
    const init: RequestInit = { method, headers, signal };
    if (body instanceof FormData) {
      // multipart : ne PAS fixer Content-Type, le navigateur ajoute le boundary.
      init.body = body;
    } else if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }

    const res = await fetch(url, init);
    if (!res.ok) {
      let detail: unknown;
      try {
        detail = await res.json();
      } catch {
        // ignore — non-JSON error body
      }
      const message =
        (detail && typeof detail === "object" && "detail" in detail
          ? String((detail as { detail?: unknown }).detail)
          : null) || `HTTP ${res.status}`;
      throw new ApiError(message, res.status, detail);
    }

    if (res.status === 204 || res.headers.get("content-length") === "0") {
      return null as T;
    }
    // Be tolerant of bodies without a content-length but no JSON content (rare).
    const text = await res.text();
    if (!text) return null as T;
    return JSON.parse(text) as T;
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
